<template>
<div>

    <!-- SSO Information Alert -->
    <v-alert
        v-if="user_forced_to_use_sso"
        type="info"
        dense
        text
        class="mb-4"
    >
        <strong>Single Sign-On (SSO) Enabled:</strong> This account is configured to use SSO for authentication.
        Password changes and two-factor authentication are managed through your organization's SSO provider.
    </v-alert>

    <v-btn
        v-on:click="showPasswordDialog"
        style="margin-right: 15px"
        color="primary"
        :disabled="user_forced_to_use_sso"
    >
        Change Password...
    </v-btn>

    <v-btn
        v-on:click="sendPasswordResetLink"
        style="margin-right: 15px"
        v-if="profile != null && profile.actor_permissions.is_organization_admin == true && profile.username_used_in_other_orgs.length == 0"
        :disabled="user_forced_to_use_sso"
    >
        Send setup account / reset password link...
    </v-btn>

    <p style="margin-top: 15px">
        <b>Two Factor Authentication (2FA)</b>
    </p>

    <v-radio-group
        v-model="two_factor_auth_method"
        v-on:change="onChange2FA"
        :disabled="user_forced_to_use_sso"
    >
        <v-radio
            label="Google Authenticator (a.k.a. TOTP / RFC 6238)"
            value="totp"
        />

        <v-radio
            label="Do not use two factor authentication"
            value="none"
        />

    </v-radio-group>

    <p v-if="user_forced_to_use_sso" class="text-caption grey--text mt-2">
        Two-factor authentication is managed through your organization's SSO provider.
    </p>

    <p
        v-if="two_factor_auth_method == 'totp' && !show_totp_secret_help"
    >
        Lost your google authenticator QR code or secret? <a v-on:click="show_totp_secret_help = true">click here</a>
    </p>

    <p
        v-if="show_totp_secret_help"
    >
        There is no way to recover a lost authenticator QR code or secret.  You can, however, generate a new one by first disabling two factor authentication and then re-enabling it (this will generate a new secret.)
    </p>

    <m-settings-organization-dialog-set-password
        ref="password_dialog"
        v-on:ok="onChangePassword"
    />

     <m-text-password-reset-link-dialog
        ref="text-password-reset-link-dialog"
    />

    <m-setup-totp-dialog
        ref="setup-totp-dialog"
        v-on:reload-profile="onReloadProfile"
    />
</div>
</template>

<script>
export default {
    props: {
        profile: {
            type: Object,
            default: function() { return {}; }
        },
    },

    data() {
        return {
            two_factor_auth_method: 'none',
            show_totp_secret_help: false,
        }
    },

    mounted () {
        if ( this.profile != null )
            this.two_factor_auth_method = this.profile.employee_account.two_factor_auth_method;
    },

    methods: {
        showPasswordDialog() {

            let organization_id = this.profile.employee_account.organization_id;
            let employee_id = this.profile.employee_account.employee_id;
            let username = this.profile.employee_account.username;

            let ask_for_old_password = !this.profile.actor_permissions.is_organization_admin &&
                                       !this.profile.actor_permissions.is_system_admin;

            if ( this.profile.username_used_in_other_orgs.length > 0 && !this.profile.actor_permissions.is_system_admin )
            {
                ask_for_old_password = true;
            }

            this.$refs['password_dialog'].showDialog(organization_id, employee_id, username, ask_for_old_password);
        },

        onChangePassword(password_data)
        {
            this.$emit('change-password',password_data);
        },

        sendPasswordResetLink()
        {
            this.$refs['text-password-reset-link-dialog'].showDialog(this.profile.employee_account.employee_id);
        },

        onChange2FA(value)
        {
            if ( value == 'none' )
            {
                this.onChange2FANone();
                return;
            }  
            
            if ( value == 'totp' ) 
            {
                this.$refs['setup-totp-dialog'].showDialog(this.profile.employee_account.employee_id);
                return;
            }
        },

        onChange2FANone()
        {
            if ( this.profile.employee_account.two_factor_auth_method == 'none' )
                return; // nothing to do, this is already the method

            this.$rc.confirm(
                'Confirm disable 2FA',
                'Are you sure that you want to disable two factor authentication (2FA)?',
                [
                    {'text': 'Cancel', 'value': 'cancel'},
                    {'text': 'Disable', 'value': 'confirm'},
                ],
            
                (response) => {
                    if (response != 'confirm') 
                    {
                        this.two_factor_auth_method = this.profile.employee_account.two_factor_auth_method;
                        return;
                    }
                    
                    this.onDisable2FA();
                }
            );
        },

        onDisable2FA()
        {
            this.$rc.showSpinner();
            
            this.$rc.rest(
                'PUT: /api/v1/auth/2fa/disable',
            
                {
                    'employee_id': this.profile.employee_account.employee_id
                },
            
                (data) => {
                    this.$rc.hideSpinner();
                    this.$emit('reload-profile');
                },
            
                () => {
                    this.$rc.hideSpinner();
                    this.$rc.alert('Error','Unable to disable 2FA');
                },
            );
        },

        onReloadProfile()
        {
            if ( this.profile != null )
                this.two_factor_auth_method = this.profile.employee_account.two_factor_auth_method;

            this.$emit('reload-profile');
        },
    },

    computed: {
        user_forced_to_use_sso() {
            if (!this.profile || !this.profile.organization_sso_settings_effective) {
                return false;
            }
            return this.profile.organization_sso_settings_effective.user_forced_to_use_sso === true;
        }
    },

    watch: {
        profile(newValue) {
            if ( newValue != null )
            {
                this.two_factor_auth_method = newValue.employee_account.two_factor_auth_method;
            }
        }
    },
}
</script>

<style lang="scss" scoped>

</style>